<template>
  <v-row
    no-gutters
    justify="center"
  >
    <v-col
      v-if="playerFormData.id"
      xl="7"
      md="9"
      class="pt-6"
    >
      <v-row
        no-gutters
        align="center"
        class="mb-6"
      >
        <v-col>
          <div class="text-h5 grey--text text--darken-2 font-weight-light  d-inline-flex align-center">
            <v-btn
              icon
              :to="{ name: $routeNames.ADMIN_PLAYERS }"
              class="mr-5"
              exact
            >
              <v-icon small>
                fal fa-arrow-left
              </v-icon>
            </v-btn>
            {{ playerFormData.first_name }} {{ playerFormData.last_name }}
          </div>
        </v-col>
        <v-col
          v-if="playerFormData.user && !playerFormData.user.is_setup_complete"
          cols="auto"
          class="pr-2"
        >
          <v-btn
            outlined
            color="grey darken-2"
            small
            class="text-none"
            :loading="mailResending"
            @click="resendInvitationEmail()"
          >
            {{ $t('button.resend_invitation') }}
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn
            outlined
            color="error"
            small
            class="text-none"
            @click="deleteConfirmationDialogVisible = true"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </v-col>
      </v-row>

      <validation-observer ref="validationObserver">
        <v-card
          class="pa-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.profile.title') }}
            </div>
            <div class="text-body-2 grey--text">
              {{ $t('new_player.profile.enter_details') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row
              no-gutters
            >
              <v-col
                class="pr-1"
                cols="auto"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    v-model="playerFormData.first_name"
                    :error-messages="errors"
                    :label="$t('text_input.data.first_name.title')"
                    name="name"
                    outlined
                    style="width: 375px"
                    append-icon="fa-light fa-address-card"
                  />
                </validation-provider>
              </v-col>
              <v-col class="pl-1">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    v-model="playerFormData.last_name"
                    :error-messages="errors"
                    :label="$t('text_input.data.last_name.title')"
                    name="last_name"
                    outlined
                    style="width: 375px"
                    append-icon="fa-light fa-address-card"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col
                cols="auto"
                class="pr-1"
              >
                <div class="text-body-2 v-label mb-2">
                  {{ $t('text_input.data.date_of_birth.title') }}
                </div>

                <div style="width: 380px">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <dob-input
                      v-model="playerFormData.birth_date"
                      :error-messages="errors"
                    />
                  </validation-provider>
                </div>
              </v-col>
              <v-col class="pl-1">
                <div class="text-body-2 v-label mb-2">
                  {{ $t('select.data.gender.title') }}
                </div>

                <div style="width: 370px">
                  <v-btn-toggle
                    v-model="playerFormData.gender"
                    color="primary"
                    mandatory
                    class="pt-1 v-btn-toggle--block"
                  >
                    <v-btn
                      value="male"
                      class="text-none"
                    >
                      <v-icon
                        left
                        small
                      >
                        fal fa-mars
                      </v-icon>
                      {{ $t('select.data.gender.male') }}
                    </v-btn>
                    <v-btn
                      value="female"
                      class="text-none"
                    >
                      <v-icon
                        left
                        small
                      >
                        fal fa-venus
                      </v-icon>
                      {{ $t('select.data.gender.female') }}
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </v-col>
            </v-row>

            <v-row
              align="end"
              no-gutters
            >
              <v-col
                cols="auto"
                class="pr-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="playerFormData.pin"
                    :error-messages="errors"
                    :label="$t('text_input.data.oib.title')"
                    name="pin"
                    style="width: 375px"
                    :placeholder="$t('text_input.data.oib.placeholder')"
                    append-icon="fa-light fa-passport"
                    outlined
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="auto"
                class="pl-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                >
                  <number-input
                    v-model="playerFormData.height"
                    outlined
                    :label="$t('text_input.data.height.title')"
                    name="height"
                    placeholder="180"
                    suffix="cm"
                    :decimal-precision="1"
                    :error-messages="errors"
                    style="width: 185px"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="auto"
                class="pl-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                >
                  <number-input
                    v-model="playerFormData.weight"
                    outlined
                    :label="$t('text_input.data.weight.title')"
                    name="weight"
                    placeholder="80"
                    :suffix="weightSuffix(user.organisation_settings.timezone)"
                    :decimal-precision="1"
                    :error-messages="errors"
                    style="width: 185px"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <!--<editor-fold desc="address input">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="playerFormData.address"
                :error-messages="errors"
                :label="$t('text_input.data.current_address.title')"
                name="address"
                :placeholder="$t('text_input.data.current_address.placeholder')"
                append-icon="fa-light fa-location-dot"
                style="width: 375px"
                outlined
              />
            </validation-provider>
            <!--</editor-fold>-->

            <!--<editor-fold desc="phone input">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="playerFormData.phone"
                :error-messages="errors"
                :label="$t('text_input.data.phone_number.title')"
                name="phone"
                :placeholder="$t('text_input.data.phone_number.placeholder')"
                append-icon="fa-light fa-mobile-screen-button"
                style="width: 375px"
                outlined
              />
            </validation-provider>
            <!--</editor-fold>-->

            <!--<editor-fold desc="birth_city input">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="playerFormData.birth_city"
                :error-messages="errors"
                name="birth_city"
                :label="$t('text_input.data.birth_city.title')"
                style="width: 375px"
                outlined
                :placeholder="$t('text_input.data.birth_city.placeholder')"
                append-icon="fa-light fa-location-arrow"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="playerFormData.nationality"
                :items="countries"
                :error="errors.length > 0"
                item-value="code"
                item-text="name"
                name="nationality"
                :label="$t('text_input.data.nationality.title')"
                outlined
                :placeholder="$t('text_input.data.nationality.placeholder')"
                style="width: 375px"
              >
                <template v-slot:item="{ item }">
                  <span class="text-capitalize">{{ item.name }}</span>
                </template>
                <template v-slot:selection="{ item }">
                  <span class="text-capitalize">{{ item.name }}</span>
                </template>
              </v-autocomplete>
            </validation-provider>
            <!--</editor-fold>-->

            <div v-if="hasParentsModule">
              <!--<editor-fold desc="parents input">-->
              <validation-provider
                v-slot="{ errors }"
              >
                <v-row no-gutters>
                  <v-col cols="auto">
                    <v-autocomplete
                      v-model="playerFormData.parent_ids"
                      :label="$t('admin.parents')"
                      outlined
                      style="width: 375px"
                      :items="parents.data"
                      :loading="parents.loading"
                      :error-messages="errors"
                      multiple
                      small-chips
                      deletable-chips
                      item-value="id"
                      :item-text="(parent) => (parent.first_name + ' ' + parent.last_name)"
                    >
                      <template v-slot:item="{ item: parent }">
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ parent.first_name }} {{ parent.last_name }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ parent.email }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                      <!--                    <template v-slot:selection="{ item: parent }">-->
                      <!--                      {{ parent.first_name }} {{ parent.last_name }}-->
                      <!--                    </template>-->
                    </v-autocomplete>
                  </v-col>
                  <v-col class="pt-3 pl-2">
                    <v-btn
                      text
                      color="primary"
                      class="text-none"
                      @click="parentDialogVisible = true"
                    >
                      {{ $t('home.add_new') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </validation-provider>
              <!--</editor-fold>-->
            </div>

            <validation-provider
              v-slot="{ errors }"
              rules="email"
            >
              <v-text-field
                v-model="playerFormData.billing_email"
                :error-messages="errors"
                :label="$t('text_input.data.reports_email.title')"
                style="width: 375px"
                outlined
                :placeholder="$t('text_input.data.reports_email.title.placeholder')"
                append-icon="fa-light fa-envelope"
                persistent-hint
                :hint="$t('text_input.data.reports_email.info')"
              />
            </validation-provider>

            <!--<editor-fold desc="school input">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="playerFormData.school"
                :error-messages="errors"
                :label="$t('text_input.data.school_name.title')"
                name="school"
                style="width: 375px"
                outlined
                :placeholder="$t('text_input.data.school_name.placeholder')"
                append-icon="fa-light fa-graduation-cap"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              ref="imageErrors"
            >
              <div class="text-body-2 v-label mb-2">
                {{ $t('media_input.data.player_photo.title') }}
              </div>
              <image-crop
                v-model="playerFormData.photo_base_64"
                :quality="2"
                :width="250"
                :height="250"
                prevent-white-space
                :placeholder="$t('add_player_photo.placeholder')"
                accept="image/png,image/jpeg"
                :placeholder-font-size="12"
                :file-size-limit="2000000"
                :initial-image="playerFormData.photo_url"
                @errors="$refs.imageErrors.setErrors($event)"
              />
              <div
                v-if="errors.length > 0"
                class="error--text caption"
              >
                {{ errors[0] }}
              </div>
            </validation-provider>
            <!--</editor-fold>-->
          </v-card-text>
        </v-card>

        <v-card
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.sports_details.title') }}
            </div>
            <div class="text-body-2 grey--text">
              {{ $t('new_player.sports_details.info') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="text-body-2 v-label mb-2">
              {{ $t('dropdown.select_team.title') }}
            </div>
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="playerFormData.team_id"
                name="team_id"
                :items="teams.data"
                item-value="id"
                item-text="name"
                :label="$t('add_new.team')"
                outlined
                style="width: 375px"
                :error-messages="errors"
                placeholder="Select..."
                @change="loadExternalPlayers()"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="playerFormData.country_code"
                name="country_code"
                :items="countries"
                item-value="code"
                item-text="name"
                :label="$t('dropdown.competition.title')"
                outlined
                style="width: 375px"
                :error-messages="errors"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="playerFormData.rank_id"
                name="team_id"
                :items="ranks.data"
                :loading="ranks.loading"
                item-value="id"
                item-text="name"
                :label="$t('text_input.data.sport_rank.label')"
                outlined
                style="width: 375px"
                :error-messages="errors"
                :placeholder="$t('text_input.data.sport_rank.placeholder')"
              >
                <template v-slot:selection="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t(`rank.${toCamelCase(item.name)}`) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t(`rank.${toCamelCase(item.name)}`) }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-select>
            </validation-provider>

            <div v-if="hasMultipleSports">
              <div class="text-body-2 v-label mb-2">
                {{ $t('card_title.player_main_sport') }}
              </div>
              <validation-provider
                v-slot="{ errors }"
              >
                <v-select
                  v-model="playerFormData.sport_id"
                  name="team_id"
                  :items="sports.data"
                  item-value="id"
                  item-text="name"
                  :label="$t('text_input.data.sport.label')"
                  outlined
                  style="width: 375px"
                  :error-messages="errors"
                  :loading="sports.loading"
                  :placeholder="$t('text_input.data.sport.placeholder')"
                />
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="playerFormData.club"
                  :label="$t('current_players_club.label')"
                  outlined
                  style="width: 375px"
                  :error-messages="errors"
                  :placeholder="$t('enter_club_name.placeholder')"
                />
              </validation-provider>
            </div>

            <!--<editor-fold desc="registration_number input">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-text-field
                v-if="user.sport && user.sport.name.toLowerCase() === 'football'"
                v-model="playerFormData.registration_number"
                :error-messages="errors"
                :label="$t('text_input.fifa_id')"
                name="registration_number"
                :placeholder="$t('text_input.fifa_id.placeholder')"
                :prefix="$t('text_input.fifa_id.title')"
                append-icon="fa-light fa-id-card-clip"
                style="width: 375px"
                outlined
              />
              <v-text-field
                v-else-if="user.sport && user.sport.name.toLowerCase() !== 'hockey'"
                v-model="playerFormData.registration_number"
                :error-messages="errors"
                :label="$t('registration_number.label')"
                name="registration_number"
                :placeholder="$t('players_registration_number.placeholder')"
                append-icon="fa-light fa-id-card-clip"
                style="width: 375px"
                outlined
              />
            </validation-provider>
            <!--</editor-fold>-->

            <validation-provider
              v-if="user.sport && user.sport.name.toLowerCase() === 'hockey'"
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="playerFormData.license_number"
                :error-messages="errors"
                :label="$t('license_number.label')"
                name="license_number"
                :placeholder="$t('players_license_number.placeholder')"
                append-icon="fa-light fa-id-card-clip"
                style="width: 375px"
                outlined
              />
            </validation-provider>

            <validation-provider
              v-if="user.sport && user.sport.name.toLowerCase() === 'tennis'"
              v-slot="{ errors }"
              rules="max_value:10000|numeric"
            >
              <v-text-field
                v-model="playerFormData.tennis_rank"
                :error-messages="errors"
                :label="playerFormData.gender === 'male' ? 'ATP Rank' : 'WTA Rank'"
                name="tennis_rank"
                :placeholder="$t('players_rank.placeholder')"
                style="width: 375px"
                type="number"
                outlined
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              rules="numeric|max_value:99|min_value:0"
            >
              <v-text-field
                v-model="playerFormData.jersey_number"
                :error-messages="errors"
                :label="$t('text_input.data.jersey.label')"
                name="pin"
                style="width: 375px"
                placeholder="10"
                append-icon="fa-light fa-shirt"
                outlined
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="playerFormData.preferred_foot_arm"
                :items="[ {name: $t('dropdown.preferred_foot.options.left')}, {name: $t('dropdown.preferred_foot.options.right')}, {name: $t('dropdown.preferred_foot.options.both')} ]"
                :error="errors.length > 0"
                item-value="name"
                item-text="name"
                name="player_position_ids"
                outlined
                :label="$t('text_input.data.preferred_foot.label')"
                :placeholder="$t('dropdown.position.placeholder')"
                style="width: 375px"
              />
            </validation-provider>

            <!--<editor-fold desc="player_position_id input">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="playerFormData.player_position_ids"
                :items="playerPositions.data"
                :loading="playerPositions.loading"
                :error="errors.length > 0"
                item-value="id"
                :item-text="(position) => position.name + ' (' + position.short_name + ')'"
                multiple
                chips
                small-chips
                deletable-chips
                name="player_position_ids"
                outlined
                :label="$t('table_headers.player_overview.position')"
                :placeholder="$t('dropdown.position.placeholder')"
                style="width: 375px"
              >
                <!--                <template-->
                <!--                  v-slot:item="{ item }"-->
                <!--                >-->
                <!--                  {{ item.name }} ({{ item.short_name }})-->
                <!--                </template>-->
                <!--                <template-->
                <!--                  v-slot:selection="{ item }"-->
                <!--                >-->
                <!--                  {{ item.name }} ({{ item.short_name }})-->
                <!--                </template>-->
              </v-select>
            </validation-provider>
            <!--</editor-fold>-->

            <!--<editor-fold desc="player_status_id">-->
            <validation-provider
              v-slot="{ errors }"
            >
              <v-select
                v-model="playerFormData.player_status_id"
                :items="playerStatuses.data"
                :loading="playerStatuses.loading"
                :error="errors.length > 0"
                item-value="id"
                name="player_status_id"
                :label="$t('dropdown.status.title')"
                outlined
                :placeholder="$t('dropdown.position.placeholder')"
                style="width: 375px"
              >
                <template v-slot:item="{ item }">
                  <span class="text-capitalize">{{ item.name }}</span>
                </template>
                <template v-slot:selection="{ item }">
                  <span class="text-capitalize">{{ item.name }}</span>
                </template>
              </v-select>
            </validation-provider>
          </v-card-text>
        </v-card>

        <v-card
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <v-row>
              <v-col v-if="!onlyPassivePlayers">
                <div class="mb-1">
                  {{ $t('new_player.user_account.title') }}
                </div>
                <div class="text-body-2 grey--text">
                  {{ $t('new_player.user_account.enter_details') }}
                </div>
              </v-col>
              <v-col v-else>
                <div class="mb-1">
                  {{ $t("card_title.user_details") }}
                </div>
              </v-col>
              <v-col
                v-if="!playerFormData.user && !onlyPassivePlayers"
                cols="auto"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-switch
                        v-model="playerFormData.create_user_account"
                        inset
                        class="d-inline-block pa-0 ma-0 font-weight-regular"
                        :label="$t('switch.create_user_account')"
                      />
                    </div>
                  </template>
                  <span>
                    {{ $t('switch.create_user_account.tooltip') }}
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="pt-5">
            <validation-provider
              v-if="(playerFormData.user && hasUsername) || playerFormData.create_user_account"
              v-slot="{ errors }"
              :rules="{ required: true, unique_username: { user_id: playerFormData.user_id || undefined }}"
            >
              <v-text-field
                v-model="playerFormData.username"
                :error-messages="errors"
                :label="$t('table_headers.admin_all_players.username')"
                name="username"
                :placeholder="$t('text_input.user.username.placeholder')"
                append-icon="fa-light fa-user"
                outlined
                style="width: 375px"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              :rules="{ required: !!(!!playerFormData.user || playerFormData.create_user_account), email: true, unique_email: { user_id: playerFormData.user_id || undefined }}"
            >
              <v-text-field
                v-model="playerFormData.email"
                :error-messages="errors"
                :label="$t('table_headers.admin_staff.email')"
                name="email"
                outlined
                autocomplete="off"
                style="width: 375px"
                append-icon="fa-light fa-envelope"
              />
            </validation-provider>

            <div v-if="playerFormData.create_user_account">
              <v-switch
                v-model="manualPassword"
                :label="$t('switch.set_password_manually')"
                persistent-hint
                :hint="manualPassword ? $t('switch.set_password_manually.player.no_instructions') : $t('switch.set_password_manually.player.instructions')"
              />

              <div
                v-if="manualPassword"
                class="mt-5"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="password"
                  rules="required|password_policy"
                >
                  <password-field
                    ref="password"
                    v-model="playerFormData.password"
                    :error-messages="errors"
                    :label="$t('text_input.login.password.title')"
                    outlined
                    type="password"
                    style="width: 375px"
                    append-icon="fa-light fa-key"
                  />
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|confirmed:password"
                >
                  <password-field
                    v-model="playerFormData.password_repeat"
                    :error-messages="errors"
                    :label="$t('text_input.user.repeat_password.title')"
                    outlined
                    type="password"
                    style="width: 375px"
                    append-icon="fa-light fa-key"
                  />
                </validation-provider>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasCatapultIntegration || hasPolarTeamIntegration || hasGpexeIntegration"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title>
            <div class="mb-1">
              {{ $t('new_player.integrations.title') }}
            </div>
            <div class="text-body-2 grey--text d-block">
              {{ $t('new_player.integrations.your_organisation') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div v-if="hasCatapultIntegration">
              <validation-provider>
                <v-autocomplete
                  v-model="playerFormData.catapult_id"
                  :items="externalPlayers.data"
                  :loading="externalPlayers.loading"
                  validation-provider
                  outlined
                  clearable
                  style="width: 375px"
                  :hint="!playerFormData.team_id ? $t('dropdown.integrations.please_select.instruction') : undefined"
                  :persistent-hint="!playerFormData.team_id"
                  :label="$t('dropdown.select_catapult_player.title')"
                  item-value="id"
                  item-text="last_name"
                >
                  <template v-slot:item="{ item: externalPlayer }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ externalPlayer.last_name }}, {{ externalPlayer.first_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ externalPlayer.jersey }} &middot; {{ externalPlayer.id }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.last_name }}, {{ item.first_name }}
                  </template>
                </v-autocomplete>
              </validation-provider>
            </div>
            <div v-if="hasPolarTeamIntegration">
              <validation-provider>
                <v-autocomplete
                  v-model="playerFormData.polar_team_id"
                  :items="externalPolarTeamPlayers.data"
                  :loading="externalPolarTeamPlayers.loading"
                  validation-provider
                  outlined
                  style="width: 375px"
                  :label="$t('dropdown.select_polar_player.title')"
                  item-value="player_id"
                  item-text="last_name"
                >
                  <template v-slot:item="{ item: externalPlayer }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ externalPlayer.last_name }}, {{ externalPlayer.first_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ externalPlayer.player_number }} &middot; {{ externalPlayer.player_id }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.last_name }}, {{ item.first_name }}
                  </template>
                </v-autocomplete>
              </validation-provider>
            </div>
            <div v-if="hasGpexeIntegration">
              <validation-provider>
                <v-autocomplete
                  v-model="playerFormData.gpexe_id"
                  :items="externalGpexePlayers.data"
                  :loading="externalGpexePlayers.loading"
                  validation-provider
                  outlined
                  style="width: 375px"
                  :label="$t('dropdown.select_gpexe_player')"
                  item-value="id"
                  item-text="name"
                >
                  <template v-slot:item="{ item: externalPlayer }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ externalPlayer.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.name }}
                  </template>
                </v-autocomplete>
              </validation-provider>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasFieldwizIntegration"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.device.title') }}
            </div>
            <div class="text-body-2 grey--text d-block">
              {{ $t('new_player.device.warning.devices_data') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="auto">
                <validation-provider v-slot="{ errors }">
                  <v-autocomplete
                    v-model="playerFormData.device_id"
                    :items="devices.data"
                    item-text="device_name"
                    item-value="id"
                    :error-messages="errors"
                    :label="$t('dropdown.serial_number.title')"
                    outlined
                    style="width: 250px"
                    :placeholder="$t('dropdown.device_id.placeholder')"
                    append-icon="fa-light fa-microchip"
                    dense
                  >
                    <template v-slot:item="{ item: device }">
                      <v-list-item-content>
                        <v-list-item-title :class="{ 'grey--text': !!device.player_id }">
                          {{ device.device_name }} &middot; {{ device.serial_number }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                cols="auto"
              >
                <v-btn
                  color="primary"
                  class="text-none"
                  text
                  @click="connectNewDevice()"
                >
                  {{ $t('button.connect_new') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasMembershipModule && $can('edit-player-membership')"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.membership_plan.title') }}
            </div>
            <div class="text-body-2 grey--text d-block">
              {{ $t('new_player.membership_plan.set_membership') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-autocomplete
              v-model="playerFormData.membership_plan_id"
              :items="membershipPlans.data"
              :loading="membershipPlans.loading"
              outlined
              style="width: 375px"
              item-value="id"
              item-text="title"
              :label="$t('dropdown.select_membership.title')"
            >
              <template v-slot:item="{ item: membershipPlan }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ membershipPlan.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ membershipPlan.currency }} {{ Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(membershipPlan.price) }} /
                    {{ membershipPlanIntervals[membershipPlan.interval].pricePer }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <div v-if="selectedMembershipPlan">
              <v-switch
                v-model="playerFormData.membership_recurring"
                class="ma-0"
                :label="$t('new_player.membership_plan.recurring.label')"
              />

              <v-menu
                v-model="membershipStartDateSelectorActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :value="playerFormData.membership_start_date ? $dayjs(playerFormData.membership_start_date).format('ll') : ''"
                      :label="$t('new_player.membership_plan.select_start_date')"
                      outlined
                      :error-messages="errors"
                      style="width: 375px"
                      :hint="$t('new_player.membership_plan.select_start_date.hint')"
                      v-on="on"
                    />
                  </validation-provider>
                </template>
                <v-date-picker
                  v-model="playerFormData.membership_start_date"
                  :first-day-of-week="1"
                  no-title
                  @change="membershipStartDateSelectorActive = false"
                />
              </v-menu>
            </div>

            <validation-provider
              v-slot="{ errors }"
              rules="email"
            >
              <v-text-field
                v-model="playerFormData.billing_email"
                :error-messages="errors"
                :label="$t('billing_email.label')"
                outlined
                :hint="$t('text_input.parent_email.hint')"
                style="width: 375px"
              />
            </validation-provider>
          </v-card-text>
        </v-card>

        <v-card
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t("card_title.player_event_settings") }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="v-list--bordered">
              <v-row
                v-if="hasPlayerTimezones"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('dropdown.current_timezone.title') }}
                </v-col>
                <v-col cols="3">
                  <v-autocomplete
                    v-model="playerFormData.settings.timezone"
                    outlined
                    :items="timezones"
                    item-value="tzCode"
                    item-text="name"
                    dense
                    hide-details
                    :placeholder="$t('dropdown.current_timezone.placeholder')"
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="hasItlModule"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.receive_morning_itl') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.receive_itl_feedback"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="hasItlModule && playerFormData.settings.receive_itl_feedback"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.morning_itl_schedule') }}
                </v-col>
                <v-col>
                  <v-btn-toggle
                    v-model="playerFormData.settings.itl_feedback_days"
                    dense
                    multiple
                    mandatory
                    color="primary"
                  >
                    <v-btn
                      :value="1"
                      class="text-none"
                    >
                      {{ $t('day.mon') }}
                    </v-btn>
                    <v-btn
                      :value="2"
                      class="text-none"
                    >
                      {{ $t('day.tue') }}
                    </v-btn>
                    <v-btn
                      :value="3"
                      class="text-none"
                    >
                      {{ $t('day.wed') }}
                    </v-btn>
                    <v-btn
                      :value="4"
                      class="text-none"
                    >
                      {{ $t('day.thu') }}
                    </v-btn>
                    <v-btn
                      :value="5"
                      class="text-none"
                    >
                      {{ $t('day.fri') }}
                    </v-btn>
                    <v-btn
                      :value="6"
                      class="text-none"
                    >
                      {{ $t('day.sat') }}
                    </v-btn>
                    <v-btn
                      :value="7"
                      class="text-none"
                    >
                      {{ $t('day.sun') }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
              <v-row
                v-if="hasItlModule && playerFormData.settings.receive_itl_feedback"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('dropdown.morning_itl_time.title') }}
                </v-col>
                <v-col cols="3">
                  <v-select
                    v-model="playerFormData.settings.itl_feedback_time"
                    outlined
                    dense
                    :items="[ '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00' ]"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="(hasUsername || playerFormData.create_user_account)"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.receive_reminder') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.receive_event_reminder"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="playerFormData.settings.receive_event_reminder && (hasUsername || playerFormData.create_user_account)"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('dropdown.how_soon_before.title') }}
                </v-col>
                <v-col cols="3">
                  <v-select
                    v-model="playerFormData.settings.event_reminder_minutes_before_event"
                    outlined
                    dense
                    :items="[ { text: $t('how_soon_settings.before.30_mins'), value: 30 }, { text: $t('how_soon_settings.before.1_hour'), value: 60 }, { text: $t('how_soon_settings.before.1.5_hours'), value: 90 }, { text: $t('how_soon_settings.before.2_hours'), value: 120 }, { text: $t('how_soon_settings.before.3_hours'), value: 180 }, { text: $t('how_soon_settings.before.4_hours'), value: 240 }]"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="hasItlModule"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.receive_rpe') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.receive_event_rpe_feedback"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="hasItlModule && playerFormData.settings.receive_event_rpe_feedback"
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('dropdown.how_soon_after.title') }}
                </v-col>
                <v-col cols="3">
                  <v-select
                    v-model="playerFormData.settings.rpe_feedback_minutes_after_event"
                    outlined
                    dense
                    :items="[ { text: $t('how_soon_settings.after.immediately'), value: 0 }, { text: $t('how_soon_settings.after.30_mins'), value: 30 }, { text: $t('how_soon_settings.after.1_hour'), value: 60 }, { text: $t('how_soon_settings.after.1.5_hours'), value: 90 }, { text: $t('how_soon_settings.after.2_hours'), value: 120 }, { text: $t('how_soon_settings.after.3_hours'), value: 180 }, { text: $t('how_soon_settings.after.4_hours'), value: 240 }]"
                    hide-details
                  />
                </v-col>
              </v-row>
              <div v-if="(hasUsername || playerFormData.create_user_account)">
                <v-row
                  align="center"
                  class="v-list-item mt-3"
                >
                  <v-col
                    cols="5"
                    class="body-2"
                  >
                    {{ $t('switch.allowed_to_create') }}
                  </v-col>
                  <v-col>
                    <v-switch
                      v-model="playerFormData.settings.create_events_allowed"
                      flat
                      hide-details
                      class="ma-0"
                    />
                  </v-col>
                </v-row>
                <v-row
                  align="center"
                  class="v-list-item"
                >
                  <v-col
                    cols="5"
                    class="body-2"
                  >
                    {{ $t('switch.allowed_to_reschedule') }}
                  </v-col>
                  <v-col>
                    <v-switch
                      v-model="playerFormData.settings.reschedule_events_allowed"
                      flat
                      hide-details
                      class="ma-0"
                    />
                  </v-col>
                </v-row>
                <v-row
                  align="center"
                  class="v-list-item"
                >
                  <v-col
                    cols="5"
                    class="body-2"
                  >
                    {{ $t('switch.receive_new_event') }}
                  </v-col>
                  <v-col>
                    <v-switch
                      v-model="playerFormData.settings.receive_new_event_notification"
                      flat
                      hide-details
                      class="ma-0"
                    />
                  </v-col>
                </v-row>
                <v-row
                  align="center"
                  class="v-list-item"
                >
                  <v-col
                    cols="5"
                    class="body-2"
                  >
                    {{ $t('switch.receive_new_media') }}
                  </v-col>
                  <v-col>
                    <v-switch
                      v-model="playerFormData.settings.receive_new_media_notification"
                      flat
                      hide-details
                      class="ma-0"
                    />
                  </v-col>
                </v-row>
                <v-row
                  align="center"
                  class="v-list-item"
                >
                  <v-col
                    cols="5"
                    class="body-2"
                  >
                    {{ $t('switch.receive_notes_updated') }}
                  </v-col>
                  <v-col>
                    <v-switch
                      v-model="playerFormData.settings.receive_event_note_updated_notification"
                      flat
                      hide-details
                      class="ma-0"
                    />
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasTrainingBuilderModule && (hasUsername || playerFormData.create_user_account)"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.training_builder_settings') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="v-list--bordered">
              <v-row
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.training_builder_settings.status_visibility') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.view_training_stats_allowed"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="(hasGpsModule || hasHrModule) && (hasUsername || playerFormData.create_user_account)"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.player_performance_settings.title') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="v-list--bordered">
              <v-row
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('new_player.player_performance_settings.allowed_to_view') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.view_performance_stats_allowed"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasDiagnosticsModule && (hasUsername || playerFormData.create_user_account)"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.diagnostics_settings') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="v-list--bordered">
              <v-row
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.allowed_to_view_diagnostics') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.view_diagnostic_stats_allowed"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasItlModule && (hasUsername || playerFormData.create_user_account)"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.wellness_settings') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="v-list--bordered">
              <v-row
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.allowed_to_view_wellness') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.view_wellness_stats_allowed"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasChatModule && (hasUsername || playerFormData.create_user_account)"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t("card_title.player_chat_settings") }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="v-list--bordered">
              <v-row
                align="center"
                class="v-list-item"
              >
                <v-col
                  cols="5"
                  class="body-2"
                >
                  {{ $t('switch.messages_to_coaches') }}
                </v-col>
                <v-col>
                  <v-switch
                    v-model="playerFormData.settings.chat_coach_allowed"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <v-row
          no-gutters
          justify="end"
          class="mt-5 mb-12"
        >
          <v-col cols="auto">
            <v-btn
              text
              large
              class="mr-2"
              exact
              :to="{ name: $routeNames.ADMIN_PLAYERS }"
            >
              {{ $t('button.cancel') }}
            </v-btn>
            <v-btn
              large
              color="primary"
              :loading="submitting"
              @click="submit()"
            >
              {{ $t("button.save_player") }}
            </v-btn>
          </v-col>
        </v-row>
      </validation-observer>
    </v-col>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deletePlayer()"
    />

    <parent-form-dialog
      :visible.sync="parentDialogVisible"
      @created="selectCreatedParent"
    />
  </v-row>
</template>

<script>
import DobInput from '@/components/helpers/DobInput'
import { PlayerApiService, PlayerPositionApiService, PlayerStatusApiService, TeamApiService } from '@/data/services'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { EventBus } from '@/plugins/bus'
import { DeviceApiService } from '@/data/services/DeviceApiService'
import { MembershipPlanApiService } from '@/data/services/MembershipPlanApiService'
import ImageCrop from '@/components/inputs/ImageCrop.vue'
import { SportApiService } from '@/data/services/SportApiService'
import { cloneDeep } from 'lodash'
import { UserApiService } from '@/data/services/UserApiService'
import { ParentApiService } from '@/data/services/ParentApiService'
import ParentFormDialog from '@/components/dialogs/ParentFormDialog.vue'
import { MembershipPlanIntervals } from '@/data/models/MembershipPlan'
import { countries } from '@/data/models/Countries'
import NumberInput from '@/components/helpers/NumberInput.vue'
import { convertWeightByTimezone, weightSuffix } from '@/components/training-builder/helpers'
import { toCamelCase } from '@/utils/helpers'
import PasswordField from '@/components/inputs/PasswordField.vue'
import timezones from 'timezones-list'

const emptySettings = {
  receive_itl_feedback: true,
  itl_feedback_days: [1, 2, 3, 4, 5, 6, 7],
  itl_feedback_time: '07:00',
  receive_event_rpe_feedback: true,
  rpe_feedback_minutes_after_event: 30,
  receive_event_reminder: true,
  event_reminder_minutes_before_event: 180,
  create_events_allowed: false,
  chat_coach_allowed: false,
  reschedule_events_allowed: false,
  view_performance_stats_allowed: false,
  view_diagnostic_stats_allowed: false,
  view_training_stats_allowed: true,
  view_wellness_stats_allowed: true,
  receive_new_event_notification: true,
  receive_new_media_notification: true,
  receive_event_note_updated_notification: true
}

const emptyPlayerFormData = {
  id: null,
  first_name: null,
  last_name: null,
  birth_date: null,
  height: null,
  weight: null,
  pin: null,
  birth_city: null,
  nationality: null,
  address: null,
  phone: null,
  school: null,
  team_id: null,
  sport_details: null,
  sport_id: null,
  rank_id: null,
  country_code: null,
  club: null,
  registration_number: null,
  license_number: null,
  tennis_rank: null,
  jersey_number: null,
  preferred_foot_arm: null,
  player_position_id: null,
  player_status_id: null,
  player_position_ids: [],
  membership_plan_id: null,
  membership_recurring: true,
  membership_start_date: null,
  membership_renewal_day: null,
  username: null,
  email: null,
  billing_email: null,
  photo: null,
  password: null,
  password_repeat: null,
  catapult_id: null,
  polar_team_id: null,
  gpexe_id: null,
  device_id: null,
  photo_base_64: null,
  settings: emptySettings,
  parent_ids: [],
  create_user_account: false
}

export default {
  name: 'AdminPlayerEditForm',
  components: {
    PasswordField,
    NumberInput,
    ParentFormDialog,
    ImageCrop,
    DeleteConfirmationDialog,
    DobInput
  },
  mixins: [deleteConfirmationMixin],
  data: () => ({
    playerFormData: emptyPlayerFormData,
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false
    },
    teams: {
      data: [],
      loading: false
    },
    externalPlayers: {
      loading: false,
      data: []
    },
    externalPolarTeamPlayers: {
      loading: false,
      data: []
    },
    externalGpexePlayers: {
      loading: false,
      data: []
    },
    devices: {
      loading: false,
      data: []
    },
    membershipPlans: {
      loading: false,
      data: []
    },
    sports: {
      loading: false,
      data: []
    },
    ranks: {
      loading: false,
      data: []
    },
    parents: {
      loading: false,
      data: []
    },
    parentDialogVisible: false,
    manualPassword: false,
    submitting: false,
    mailResending: false,
    mailResent: false,
    hasUsername: false,
    membershipStartDateSelectorActive: false,
    countries: countries
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasMembershipModule() {
      return this.user.organisation_settings.has_membership_module
    },
    hasMultipleSports() {
      return this.user.organisation_settings.has_multiple_sports
    },
    hasCatapultIntegration() {
      return (this.user.organisation_settings.integrations || []).find(i => i.key === 'catapult')
    },
    hasPolarTeamIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'polar-team')
    },
    hasGpexeIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'gpexe')
    },
    hasFieldwizIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'fieldwiz')
    },
    hasParentsModule() {
      return this.user.organisation_settings.has_parents_module
    },
    hasChatModule() {
      return this.user.organisation_settings.has_chat_module
    },
    hasTrainingBuilderModule() {
      return this.user.organisation_settings.has_training_builder_module
    },
    hasDiagnosticsModule() {
      return this.user.organisation_settings.has_diagnostics_module
    },
    hasGpsModule() {
      return this.user.organisation_settings.has_gps_module
    },
    hasHrModule() {
      return this.user.organisation_settings.has_heart_rate_module
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module
    },
    onlyPassivePlayers() {
      return this.user.organisation_settings.only_passive_players ?? false
    },
    hasPlayerTimezones() {
      return this.user.organisation_settings.has_player_timezones ?? false
    },
    selectedMembershipPlan() {
      if (!this.playerFormData.membership_plan_id) return undefined

      return this.membershipPlans.data.find(mp => mp.id === this.playerFormData.membership_plan_id)
    }
  },
  created() {
    this.membershipPlanIntervals = MembershipPlanIntervals

    this.timezones = [{ tzCode: null, name: 'Same as organisation' }, ...timezones]

    this.fetchPlayer()
    this.fetchTeams()
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
    this.fetchDevices()
    this.fetchMembershipPlans()
    this.fetchSports()
    this.fetchRanks()
    this.fetchParents()

    EventBus.$on([this.$events.DEVICE_CREATED, this.$events.DEVICE_UPDATED], (event) => {
      this.fetchDevices()
    })
  },
  methods: {
    toCamelCase,
    weightSuffix,
    async fetchPlayer() {
      try {
        const result = await PlayerApiService.getPlayerDetails(this.$route.params.id)

        result.data.weight = convertWeightByTimezone(result.data.weight, this.user.organisation_settings.timezone, true)

        const data = { ...emptyPlayerFormData, ...result.data }

        if (!data.settings) {
          data.settings = cloneDeep(emptySettings)
        }

        data.parent_ids = (data.parents || []).map(p => p.id)
        data.player_position_ids = (data.player_positions || []).map(p => p.id)
        data.rank_id = data.rank?.id

        if (!data.membership_start_date) {
          data.membership_start_date = this.$dayjs().startOf('month').format('YYYY-MM-DD')
        } else {
          data.membership_start_date = this.$dayjs(data.membership_start_date).format('YYYY-MM-DD')
        }

        this.playerFormData = data

        this.hasUsername = data.username !== null

        this.loadExternalPlayers()
        this.loadExternalPolarTeamPlayers()
        this.loadExternalGpexePlayers()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      }
    },
    async fetchDevices() {
      try {
        this.devices.loading = true

        const response = await DeviceApiService.getAll()

        this.devices.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_devices.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.devices.loading = false
      }
    },
    async fetchTeams() {
      try {
        this.teams.loading = true

        const response = await TeamApiService.getAll()

        this.teams.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teams.loading = false
      }
    },
    async loadExternalPlayers() {
      if (!this.hasCatapultIntegration) return

      try {
        this.externalPlayers.loading = true

        this.externalPlayers.data = await PlayerApiService.getExternalPlayers(this.playerFormData.team_id)
      } catch (e) {

      } finally {
        this.externalPlayers.loading = false
      }
    },
    async loadExternalPolarTeamPlayers() {
      if (!this.hasPolarTeamIntegration) return

      try {
        this.externalPolarTeamPlayers.loading = true

        this.externalPolarTeamPlayers.data = await PlayerApiService.getExternalPolarTeamPlayers()
      } catch (e) {

      } finally {
        this.externalPolarTeamPlayers.loading = false
      }
    },
    async loadExternalGpexePlayers() {
      if (!this.hasGpexeIntegration) return

      try {
        this.externalGpexePlayers.loading = true

        this.externalGpexePlayers.data = await PlayerApiService.getExternalGpexePlayers()
      } catch (e) {

      } finally {
        this.externalGpexePlayers.loading = false
      }
    },
    async fetchMembershipPlans() {
      try {
        this.membershipPlans.loading = true

        const result = await MembershipPlanApiService.getAll()

        this.membershipPlans.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_membership_plans.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.membershipPlans.loading = false
      }
    },
    async submit() {
      const validation = await this.$refs.validationObserver.validate()

      if (!validation) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        this.playerFormData.weight = convertWeightByTimezone(this.playerFormData.weight, this.user.organisation_settings.timezone)

        await PlayerApiService.update(this.playerFormData)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_updated.success'),
          color: 'success'
        })

        this.$router.push({ name: this.$routeNames.ADMIN_PLAYERS })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_edit.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short_name: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchSports() {
      try {
        this.sports.loading = true

        this.sports.data = await SportApiService.getAll()
        this.sports.data = this.sports.data.map(sport => {
          return {
            ...sport,
            name: this.$t(`sport.${sport.name.toLowerCase()}`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.sports.loading = false
      }
    },
    async fetchRanks() {
      try {
        this.ranks.loading = true

        this.ranks.data = await SportApiService.getAllRanks()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.ranks.loading = false
      }
    },
    async fetchParents() {
      try {
        this.parents.loading = true

        const response = await ParentApiService.getAll({})

        this.parents.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.parents.loading = false
      }
    },
    async deletePlayer() {
      try {
        this.deleting = true

        await PlayerApiService.delete(this.playerFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_delete.success'),
          color: 'success'
        })

        this.$router.push({ name: this.$routeNames.ADMIN_PLAYERS })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    connectNewDevice() {
      EventBus.$emit(this.$events.OPEN_DIALOG_DEVICE)
    },
    async resendInvitationEmail() {
      if (!this.playerFormData.user_id) return

      try {
        this.mailResending = true

        await UserApiService.resendInvitationEmail(this.playerFormData.user_id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.email_send.success'),
          color: 'success'
        })

        this.$router.push({ name: this.$routeNames.ADMIN_PLAYERS })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.email_send.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.mailResending = false
      }
    },
    selectCreatedParent(parent) {
      this.fetchParents()

      this.playerFormData.parent_ids.push(parent.id)
    }
  }
}
</script>
